import React, { useState } from 'react';
import styles from './BudgetLineSelectionModal.module.css';
import { EyeIcon } from "../icons/eye-icon";
import { useModal } from "../../contexts/ModalContext";

/**
 * Modal para seleccionar líneas de presupuesto
 * Props:
 * - open: boolean (si el modal está visible)
 * - budgetLines: array (lista de líneas de presupuesto disponibles)
 * - onConfirm: función para confirmar la selección (recibe array de líneas seleccionadas)
 * - onCancel: función para cancelar la selección
 */
export default function BudgetLineSelectionModal({ 
  open, 
  budgetLines = [], 
  onConfirm, 
  onCancel 
}) {
  const { openModal } = useModal();
  const [selectedLines, setSelectedLines] = useState([]);

  const handleLineToggle = (line) => {
    setSelectedLines(prev => {
      const isSelected = prev.some(l => l.id === line.id);
      
      if (isSelected) {
        // Deseleccionar
        return prev.filter(l => l.id !== line.id);
      } else {
        // Validar que todas las líneas tengan el mismo numPresupuesto
        if (prev.length > 0) {
          const firstNumPresupuesto = prev[0].numPresupuesto;
          if (line.numPresupuesto !== firstNumPresupuesto) {
            // No permitir seleccionar líneas con diferente numPresupuesto
            alert(`Solo se pueden seleccionar líneas del mismo presupuesto. El presupuesto seleccionado es ${firstNumPresupuesto}.`);
            return prev;
          }
        }
        // Seleccionar la nueva línea
        return [...prev, line];
      }
    });
  };

  const isLineSelected = (lineId) => {
    return selectedLines.some(l => l.id === lineId);
  };

  const handleConfirm = () => {
    if (selectedLines.length > 0 && onConfirm) {
      onConfirm(selectedLines);
      setSelectedLines([]);
    }
  };

  const handleCancel = () => {
    setSelectedLines([]);
    if (onCancel) {
      onCancel();
    }
  };

  if (!open) return null;

  // Mostrar solo líneas que NO estén marcadas como "noRealizar" (es decir,
  // aquellas con `noRealizar` distinto de true). Esto evita mostrar al usuario
  // líneas que no deben realizarse.
  const visibleLines = (budgetLines || []).filter(line => line && line.noRealizar !== true);

  return (
    <div className={styles.overlay} onClick={handleCancel}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <h3 className={styles.title}>
            Selecciona líneas de presupuesto
            {selectedLines.length > 0 && ` (${selectedLines.length} seleccionada${selectedLines.length > 1 ? 's' : ''})`}
          </h3>
          <button className={styles.closeButton} onClick={handleCancel}>
            ✕
          </button>
        </div>

        {/* Buscador */}
        <div className={styles.searchContainer}>
          <input 
            type="text" 
            placeholder="Buscar" 
            className={styles.searchInput}
          />
        </div>

        {/* Lista de líneas de presupuesto */}
        <div className={styles.linesList}>
          {visibleLines.length === 0 ? (
            <div className={styles.noLines}>
              <p>No hay líneas de presupuesto disponibles</p>
            </div>
          ) : (
            <>
              {/* Fila de encabezado */}
              <div className={styles.headerRow}>
                <div className={styles.lineHeader}>
                  <div className={styles.lineIcon}></div>
                  <div className={styles.lineInfo}>
                    <div className={styles.lineGremio}>Gremio</div>
                    <div className={styles.lineDetails}>
                      <span className={styles.lineLabel}>Num. presupuesto</span>
                      <span className={styles.lineSeparator}>·</span>
                      <span className={styles.lineLabel}>Coste</span>
                      <span className={styles.lineSeparator}>·</span>
                      <span className={styles.lineLabel}>Presupuesto</span>
                      <span className={styles.lineSeparator}>·</span>
                      <span className={styles.lineLabel}>Conjunto</span>
                      <span className={styles.lineSeparator}>·</span>
                      <span className={styles.lineLabel}>Beneficio</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {visibleLines.map((line) => (
                <div
                  key={line.id}
                  className={`${styles.lineItem} ${
                    isLineSelected(line.id) ? styles.selected : ''
                  }`}
                  onClick={() => handleLineToggle(line)}
                >
                  <div className={styles.lineHeader}>
                    <div className={styles.lineIcon}>
                      {isLineSelected(line.id) ? '✓' : '⚪'}
                    </div>
                    <div className={styles.lineInfo}>
                      <div className={styles.lineGremio}>{line.gremio}</div>
                      <div className={styles.lineDetails}>
                        <span className={styles.lineLabel}>{line.numPresupuesto}</span>
                        <span className={styles.lineSeparator}>·</span>
                        <span className={styles.lineLabel}>€ {line.costeTotal.toFixed(2)}</span>
                        <span className={styles.lineSeparator}>·</span>
                        <span className={styles.lineLabel}>€ {line.presupuestoTotal.toFixed(2)}</span>
                        <span className={styles.lineSeparator}>·</span>
                        <span className={styles.lineValue}>{line.conjuntoTotal.toFixed(4)}</span>
                        <span className={styles.lineSeparator}>·</span>
                        <span className={styles.lineLabel}>€ {line.beneficioTotal.toFixed(2)}</span>
                      </div>
                    </div>
                    <div className={styles.lineActions}>
                      <button
                        className={styles.eyeButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (openModal) openModal('LineDetailModal', { lineId: line.id });
                        }}
                        title="Ver trabajos"
                      >
                        <EyeIcon width={18} height={18} color="#333" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Acciones */}
        <div className={styles.actions}>
          <button 
            className={`${styles.actionButton} ${styles.confirmButton}`} 
            onClick={handleConfirm}
            disabled={selectedLines.length === 0}
          >
            Confirmar {selectedLines.length > 0 && `(${selectedLines.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}
